import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { Theme } from '@material-ui/core/styles/createTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Box from '@material-ui/core/Box/Box';

import compose from 'helpers/compose';
import ErrorSnackbar from 'components/ErrorSnackbar/ErrorSnackbar';
import withLoading from 'components/Loading/withLoading';
import SessionExpiringDialog from 'components/SessionExpired/SessionExpiringDialog';
import LoginExpiringDialog from 'components/SessionExpired/LoginExpiringDialog';
import Alert from 'components/Alert/Alert';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { RootStore } from 'stores/rootStore';
import { UIStore } from 'stores/uiStore';
import Header from './Header';
import Content from './Content';
import { ViewKeys } from 'stores/userStoreKC';
//import { AdvisorMenuItems } from 'components/AdvisorMenu/AdvisorMenu';
import { Permission } from 'helpers/permissions';

const useAlertStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      position: 'fixed',
      bottom: '0',
      left: 0,
      width: '100%',
      backgroundColor: 'white',
      zIndex: 9500,
    },
    paddedBox: {
      padding: theme.spacing(9),
    },
    alertText: {
      margin: 0,
    },
  }),
);

interface Props {
  rootStore: RootStore;
  mockAccount?: boolean;
  uiStore: UIStore;
  title?: string;
  detail?: boolean;
  signatureOnly?: boolean;
  admin?: boolean;
  noBreadcrumbs?: boolean;
}

const LayoutContainer = withLoading(({ children }) => {
  return <div style={{ minHeight: '100%'}}>{children}</div>;
});

const Layout: React.FC<Props> = ({
  uiStore,
  rootStore,
  title,
  mockAccount,
  detail,
  admin,
  noBreadcrumbs,
  signatureOnly,
  ...contentProps
}) => {
  const [logoutDialogShown, setLogoutDialogShown] = useState(false);
  const alertClasses = useAlertStyles();
  const { t } = useTranslation();

  useEffect(() => {
    uiStore.setWindowTitle(title);
  }, [title, uiStore]);

  return (
    <>
      <LayoutContainer loading={uiStore.loading} loadingText={uiStore.loadingText} detail={detail} open={uiStore.detailMenuOpen} signatureOnly={signatureOnly}>
        <Header showLogoutDialog={() => setLogoutDialogShown(true)} admin={admin} signatureOnly={signatureOnly} />
        <Content
          {...contentProps}
          rootStore={rootStore}
          logoutDialogShown={logoutDialogShown}
          hideLogoutDialog={() => setLogoutDialogShown(false)}
          detail={detail}
          signatureOnly={signatureOnly}
          noBreadcrumbs={noBreadcrumbs}
        />
        <ErrorSnackbar autoHideDuration={10000} />
        <SessionExpiringDialog />
        <LoginExpiringDialog />
        <ConfirmDialog
          isOpen={uiStore.helpDialogShown}
          title="help_dialog_title"
          close={() => uiStore.setHelpDialogShown(false)}
          btnCloseText="general_close"
        >
          <div
            dangerouslySetInnerHTML={{
              __html: t('help_dialog_content', {
                interpolation: { escapeValue: false },
              }),
            }}
          />
        </ConfirmDialog>

        <div className={alertClasses.root}>
          {mockAccount && (
            <Alert>
              <p
                className={alertClasses.alertText}
                dangerouslySetInnerHTML={{
                  __html: t('accountMock_infoText', { interpolation: { escapeValue: false } }),
                }}
              />
            </Alert>
          )}
          {rootStore.stores.userStore.view[ViewKeys.DEMO_MODE] && (
            <Alert>
              <p
                className={alertClasses.alertText}
                dangerouslySetInnerHTML={{
                  __html: t('demoMode_infoText', { interpolation: { escapeValue: false } }),
                }}
              />
            </Alert>
          )}
        </div>
        {(mockAccount || rootStore.stores.userStore.view[ViewKeys.DEMO_MODE]) && (
          <Box className={alertClasses.paddedBox} />
        )}
        {rootStore.stores.userStore.checkPermissions(Permission.APP_ADVISOR) && ''}
      </LayoutContainer>
    </>
  );
};

export default compose(inject('uiStore', 'rootStore'), observer)(Layout);
